import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { StripeElementLocale } from "@stripe/stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createCheckoutPayment,
  checkPromoCode,
  PublicCheckoutDetails,
} from "../data/checkout";
import { getTrackingMetadata } from "../utils/tracking";

// Map i18n language codes to Stripe locale codes
const getStripeLocale = (lang: string): StripeElementLocale => {
  const localeMap: Record<string, StripeElementLocale> = {
    en: "en",
    fr: "fr",
    es: "es",
    de: "de",
    pt: "pt",
    it: "it",
  };
  return localeMap[lang] || "auto";
};

export interface CheckoutFormData {
  email: string;
  name: string;
  phone: string;
  promotionCode: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

interface CheckoutPaymentFormProps {
  checkoutData: PublicCheckoutDetails;
  stripeInstance: Stripe;
  onSuccess: () => void;
  onAmountChange?: (amount: number) => void;
  selectedCurrency?: string;
  initialFormData?: Partial<CheckoutFormData>;
  onFormDataChange?: (data: CheckoutFormData) => void;
}

type PromoStatus = "idle" | "checking" | "valid" | "invalid";

// Inner component that uses Stripe hooks (must be inside Elements provider)
function CheckoutPaymentFormInner({
  checkoutData,
  billingDetails,
  onSuccess,
}: {
  checkoutData: PublicCheckoutDetails;
  billingDetails: CheckoutFormData;
  onSuccess: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const brandColor = checkoutData.business.brandColor || "#eb5e27";
  const collectBillingAddress = checkoutData.options?.collectBillingAddress;

  const handlePaymentSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    try {
      // Pass the billing details we already collected on the previous step to
      // Stripe's confirmPayment. Without this, Stripe's PaymentElement tries to
      // re-collect and re-validate the address itself and rejects valid
      // addresses (especially French ones) with generic "invalid" errors.
      const addressForStripe = collectBillingAddress
        ? {
            line1: billingDetails.address.line1 || null,
            line2: billingDetails.address.line2 || null,
            city: billingDetails.address.city || null,
            state: billingDetails.address.state || null,
            postal_code: billingDetails.address.postal_code || null,
            country: billingDetails.address.country || null,
          }
        : undefined;

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              email: billingDetails.email || undefined,
              name: billingDetails.name || undefined,
              phone: billingDetails.phone || undefined,
              ...(addressForStripe ? { address: addressForStripe } : {}),
            },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(
          error.message || t("gymkeePay.paymentForm.paymentFailed"),
        );
      } else if (paymentIntent) {
        if (
          paymentIntent.status === "succeeded" ||
          paymentIntent.status === "processing"
        ) {
          onSuccess();
        } else if (paymentIntent.status === "requires_action") {
          setErrorMessage(t("gymkeePay.paymentForm.authenticationRequired"));
        } else {
          setErrorMessage(
            t("gymkeePay.paymentForm.paymentStatus", {
              status: paymentIntent.status,
            }),
          );
        }
      }
    } catch (error: unknown) {
      console.error("Payment error:", error);
      setErrorMessage(t("gymkeePay.paymentForm.unexpectedError"));
    } finally {
      setIsProcessing(false);
    }
  };

  const buttonText =
    checkoutData.mode === "subscription"
      ? t("gymkeePay.checkout.subscribe", { defaultValue: "Subscribe" })
      : t("gymkeePay.checkout.pay", { defaultValue: "Pay" });

  return (
    <form onSubmit={handlePaymentSubmit}>
      <div className="mb-4">
        <PaymentElement options={{ layout: "tabs" }} />
      </div>

      {errorMessage && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full cursor-pointer rounded-lg px-4 py-3 font-medium text-white transition-all disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: brandColor }}
      >
        {isProcessing ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="h-5 w-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {t("gymkeePay.paymentForm.processing")}
          </span>
        ) : (
          buttonText
        )}
      </button>
    </form>
  );
}

function CheckoutPaymentForm({
  checkoutData,
  stripeInstance,
  onSuccess,
  onAmountChange,
  selectedCurrency,
  initialFormData,
  onFormDataChange,
}: CheckoutPaymentFormProps) {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState(initialFormData?.email ?? "");
  const [name, setName] = useState(initialFormData?.name ?? "");
  const [phone, setPhone] = useState(initialFormData?.phone ?? "");
  const [promotionCode, setPromotionCode] = useState(
    initialFormData?.promotionCode ?? "",
  );
  const [address, setAddress] = useState(
    initialFormData?.address ?? {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
    },
  );
  const [isInitializing, setIsInitializing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // Promo code validation state
  const [promoStatus, setPromoStatus] = useState<PromoStatus>("idle");
  const [promoError, setPromoError] = useState<string | null>(null);
  // Track the code that was validated so we can detect changes
  const [validatedCode, setValidatedCode] = useState<string | null>(null);

  const brandColor = checkoutData.business.brandColor || "#eb5e27";
  const stripeLocale = getStripeLocale(i18n.language);
  const options = checkoutData.options;

  // Notify parent of form data changes so it can persist across currency switches
  useEffect(() => {
    onFormDataChange?.({ email, name, phone, promotionCode, address });
  }, [email, name, phone, promotionCode, address]);

  // Read promo code from URL parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const promoFromUrl =
      urlParams.get("promo") ||
      urlParams.get("code") ||
      urlParams.get("coupon");
    if (promoFromUrl && !promotionCode) {
      setPromotionCode(promoFromUrl);
    }
  }, []);

  // Reset promo validation when the code changes
  const handlePromoCodeChange = (value: string) => {
    setPromotionCode(value);
    if (validatedCode && value !== validatedCode) {
      setPromoStatus("idle");
      setPromoError(null);
      setValidatedCode(null);
    }
  };

  const handleAddressChange =
    (field: keyof typeof address) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setAddress((prev) => ({ ...prev, [field]: e.target.value }));
    };

  // Validate promo code without creating payment
  const handleApplyPromo = async () => {
    if (!promotionCode.trim()) return;

    setPromoStatus("checking");
    setPromoError(null);

    try {
      const result = await checkPromoCode(
        checkoutData.paymentLinkId,
        promotionCode.trim(),
        selectedCurrency,
      );
      setPromoStatus("valid");
      setValidatedCode(promotionCode.trim());
      // Update parent with the preview discounted amount
      if (onAmountChange && result.discountedAmount !== undefined) {
        onAmountChange(result.discountedAmount);
      }
    } catch (error: unknown) {
      setPromoStatus("invalid");
      const axiosError = error as { response?: { data?: { message?: string } } };
      setPromoError(
        axiosError.response?.data?.message ||
          t("gymkeePay.checkout.promoCodeInvalid", {
            defaultValue: "Invalid or expired promotion code",
          }),
      );
    }
  };

  const handleStartPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!email) {
      setErrorMessage(
        t("gymkeePay.checkout.emailRequired", {
          defaultValue: "Email is required",
        }),
      );
      return;
    }

    // Validate address if required
    if (options?.collectBillingAddress) {
      if (
        !address.line1 ||
        !address.city ||
        !address.postal_code ||
        !address.country
      ) {
        setErrorMessage(
          t("gymkeePay.checkout.addressRequired", {
            defaultValue: "Please fill in the required address fields",
          }),
        );
        return;
      }
    }

    // Validate phone if required
    if (options?.collectPhoneNumber && !phone) {
      setErrorMessage(
        t("gymkeePay.checkout.phoneRequired", {
          defaultValue: "Phone number is required",
        }),
      );
      return;
    }

    setIsInitializing(true);

    try {
      const response = await createCheckoutPayment(
        checkoutData.paymentLinkId,
        {
          email,
          name: name || undefined,
          phone: options?.collectPhoneNumber ? phone || undefined : undefined,
          address: options?.collectBillingAddress
            ? {
                line1: address.line1,
                line2: address.line2 || undefined,
                city: address.city,
                state: address.state || undefined,
                postal_code: address.postal_code,
                country: address.country,
              }
            : undefined,
          // Always send promo code if entered - backend will validate if allowed
          promotionCode: promotionCode || undefined,
          currency: selectedCurrency || undefined,
          metadata: await getTrackingMetadata(),
        },
      );
      // Notify parent of the final amount (may be discounted if promo code applied)
      if (onAmountChange && response.amount !== undefined) {
        onAmountChange(response.amount);
      }

      // If amount is 0 (fully discounted), no payment needed - go directly to success
      if (!response.clientSecret && response.amount === 0) {
        onSuccess();
        return;
      }

      setClientSecret(response.clientSecret);
    } catch (error: unknown) {
      console.error("Failed to initialize payment:", error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      setErrorMessage(
        axiosError.response?.data?.message ||
          t("gymkeePay.paymentForm.failedToInitialize"),
      );
    } finally {
      setIsInitializing(false);
    }
  };

  const showPromoField = options?.allowPromotionCodes || promotionCode;

  // Show email/name form first, then payment element
  if (!clientSecret) {
    return (
      <form onSubmit={handleStartPayment}>
        <div className="space-y-3">
          {/* Email */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("gymkeePay.checkout.emailLabel", { defaultValue: "Email" })}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("gymkeePay.checkout.emailPlaceholder", {
                defaultValue: "your@email.com",
              })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>

          {/* Name */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("gymkeePay.checkout.nameLabel", { defaultValue: "Name" })}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("gymkeePay.checkout.namePlaceholder", {
                defaultValue: "John Doe",
              })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Phone (conditional) */}
          {options?.collectPhoneNumber && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t("gymkeePay.checkout.phoneLabel", {
                  defaultValue: "Phone number",
                })}{" "}
                *
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t("gymkeePay.checkout.phonePlaceholder", {
                  defaultValue: "+33 6 12 34 56 78",
                })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
          )}

          {/* Billing Address (conditional) */}
          {options?.collectBillingAddress && (
            <div className="space-y-3 border-t border-gray-100 pt-3">
              <p className="text-sm font-medium text-gray-700">
                {t("gymkeePay.checkout.billingAddress", {
                  defaultValue: "Billing address",
                })}
              </p>

              <div>
                <input
                  type="text"
                  value={address.line1}
                  onChange={handleAddressChange("line1")}
                  placeholder={t("gymkeePay.checkout.addressLine1", {
                    defaultValue: "Address line 1",
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <input
                  type="text"
                  value={address.line2}
                  onChange={handleAddressChange("line2")}
                  placeholder={t("gymkeePay.checkout.addressLine2", {
                    defaultValue: "Address line 2 (optional)",
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={address.city}
                  onChange={handleAddressChange("city")}
                  placeholder={t("gymkeePay.checkout.city", {
                    defaultValue: "City",
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
                <input
                  type="text"
                  value={address.postal_code}
                  onChange={handleAddressChange("postal_code")}
                  placeholder={t("gymkeePay.checkout.postalCode", {
                    defaultValue: "Postal code",
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  value={address.state}
                  onChange={handleAddressChange("state")}
                  placeholder={t("gymkeePay.checkout.state", {
                    defaultValue: "State/Province",
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={address.country}
                  onChange={handleAddressChange("country")}
                  placeholder={t("gymkeePay.checkout.country", {
                    defaultValue: "Country (e.g. FR)",
                  })}
                  maxLength={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm uppercase focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          )}

          {/* Promotion Code - show if allowed OR if a code was pre-filled (e.g., from URL) */}
          {showPromoField && (
            <div className="border-t border-gray-100 pt-3">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                {t("gymkeePay.checkout.promoCode", {
                  defaultValue: "Promotion code",
                })}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promotionCode}
                  onChange={(e) => handlePromoCodeChange(e.target.value)}
                  placeholder={t("gymkeePay.checkout.promoCodePlaceholder", {
                    defaultValue: "Enter code",
                  })}
                  className={`flex-1 rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-1 ${
                    promoStatus === "valid"
                      ? "border-green-400 focus:border-green-500 focus:ring-green-500"
                      : promoStatus === "invalid"
                        ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  }`}
                  disabled={promoStatus === "checking"}
                />
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  disabled={
                    !promotionCode.trim() ||
                    promoStatus === "checking" ||
                    promoStatus === "valid"
                  }
                  className="shrink-0 cursor-pointer rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm font-medium text-gray-700 transition-all hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {promoStatus === "checking"
                    ? t("gymkeePay.checkout.checking", {
                        defaultValue: "Checking...",
                      })
                    : t("gymkeePay.checkout.apply", {
                        defaultValue: "Apply",
                      })}
                </button>
              </div>
              {/* Promo validation feedback */}
              {promoStatus === "valid" && (
                <p className="mt-1.5 flex items-center gap-1 text-sm text-green-600">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {t("gymkeePay.checkout.promoCodeApplied", {
                    defaultValue: "Promotion code applied",
                  })}
                </p>
              )}
              {promoStatus === "invalid" && promoError && (
                <p className="mt-1.5 text-sm text-red-600">{promoError}</p>
              )}
            </div>
          )}
        </div>

        {errorMessage && (
          <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-600">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={isInitializing || !email}
          className="mt-4 w-full cursor-pointer rounded-lg px-4 py-3 font-medium text-white transition-all disabled:cursor-not-allowed disabled:opacity-50"
          style={{ backgroundColor: brandColor }}
        >
          {isInitializing ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="h-5 w-5 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t("gymkeePay.paymentForm.processing")}
            </span>
          ) : (
            t("gymkeePay.checkout.continue", { defaultValue: "Continue" })
          )}
        </button>
      </form>
    );
  }

  // Payment element form
  return (
    <Elements
      stripe={stripeInstance}
      options={{
        clientSecret,
        locale: stripeLocale,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: brandColor,
            borderRadius: "8px",
          },
        },
        loader: "auto",
      }}
    >
      <CheckoutPaymentFormInner
        checkoutData={checkoutData}
        billingDetails={{ email, name, phone, promotionCode, address }}
        onSuccess={onSuccess}
      />
    </Elements>
  );
}

export default CheckoutPaymentForm;
